import { create } from "zustand";
import { persist } from "zustand/middleware";

// Minimal interface — compatible with all Product types across the codebase
export interface CartProduct {
  id: number;
  price?: number | null;
  discount?: number | null;
  [key: string]: any;
}

export interface CartItem {
  id: number;
  product_id: number;
  quantity: number;
  product: CartProduct;
  variant_id?: number;
}

interface CartState {
  items: CartItem[];
  loading: boolean;
  error: string | null;
  addToCart: (
    product: CartProduct,
    quantity?: number,
    variantId?: number
  ) => void;
  removeFromCart: (productId: number, variantId?: number) => void;
  updateQuantity: (productId: number, quantity: number, variantId?: number) => void;
  clearCart: () => void;
  syncFromDB: (dbItems: Array<{ product_id: number; qty: number; usd_price?: number; sale_rate?: number; name?: string; product_variant_id?: number }>) => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      loading: false,
      error: null,

      addToCart: (product, quantity = 1, variantId) => {
        const { items } = get();
        const existingIndex = items.findIndex(
          (item) =>
            item.product_id === product.id &&
            item.variant_id === variantId
        );

        if (existingIndex > -1) {
          const stock = product.quantity;
          const isUnlimited = product.is_unlimited_stock;
          const cap = isUnlimited ? 99 : stock;
          set({
            error: null,
            items: items.map((item, i) =>
              i === existingIndex
                ? { ...item, quantity: cap != null ? Math.min(item.quantity + quantity, cap) : item.quantity + quantity }
                : item
            ),
          });
        } else {
          set({
            error: null,
            items: [
              ...items,
              {
                id: Date.now(),
                product_id: product.id,
                quantity,
                product,
                variant_id: variantId,
              },
            ],
          });
        }
      },

      removeFromCart: (productId, variantId) => {
        set((state) => ({
          error: null,
          items: state.items.filter(
            (item) => !(item.product_id === productId && item.variant_id === variantId)
          ),
        }));
      },

      updateQuantity: (productId, quantity, variantId) => {
        if (quantity <= 0) {
          get().removeFromCart(productId, variantId);
          return;
        }
        const item = get().items.find(
          (i) => i.product_id === productId && i.variant_id === variantId
        );
        const stock = item?.product?.quantity;
        const isUnlimited = item?.product?.is_unlimited_stock;
        const cap = isUnlimited ? 99 : stock;
        const capped = cap != null ? Math.min(quantity, cap) : quantity;
        set((state) => ({
          error: null,
          items: state.items.map((i) =>
            i.product_id === productId && i.variant_id === variantId
              ? { ...i, quantity: capped }
              : i
          ),
        }));
      },

      clearCart: () => {
        set({ items: [], error: null });
      },

      syncFromDB: (dbItems) => {
        set({
          items: dbItems.map((item) => ({
            id: item.product_id,
            product_id: item.product_id,
            quantity: item.qty,
            product: {
              id: item.product_id,
              price: item.usd_price ?? null,
              discount: item.sale_rate != null ? item.sale_rate / 100 : null,
              name: item.name,
            },
            variant_id: item.product_variant_id ?? undefined,
          })),
          error: null,
        });
      },
    }),
    { name: "cart" }
  )
);
